import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Form, Input, Text, TextButton, Title, toastService } from '@thefarmersfront/kitchen';

import { BrandLogo, InfoText, InputLabel } from '~/components/common';

import { usePostUserFindPassword } from '~/hooks/auth';
import { useLoadingStore } from '~/store';
import { validateEmailFormat, validateIdFormat, validateNoSpace } from '~/utils';

import { FIND_PASSWORD, FIND_PASSWORD_INFO_TEXT, PATH } from '~/constants';

import type { TypeErrorResponseData, TypeInputState, TypeInputStatus } from '~/types/common.types';

import styles from './FindPasswordPage.module.scss';

const FindPasswordPage = () => {
  const navigate = useNavigate();
  const { postUserFindPassword } = usePostUserFindPassword();
  const { setIsLoading } = useLoadingStore();

  const [loginId, setLoginId] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  const [loginIdInputState, setLoginIdInputState] = useState<TypeInputState>({
    status: 'default',
  });
  const [ownerEmailInputState, setOwnerEmailInputState] = useState<TypeInputState>({
    status: 'default',
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAllFieldsStatusAtOnce('default');

    const isAllFieldsFilled = loginId && ownerEmail;

    if (!isAllFieldsFilled) {
      validateEmptyFields();

      return;
    }

    const isLoginIdValid = validateNoSpace(loginId) && validateIdFormat(loginId) && loginId.length <= 100;
    const isOwnerEmailValid = validateNoSpace(ownerEmail) && validateEmailFormat(ownerEmail);

    if (!(isLoginIdValid && isOwnerEmailValid)) {
      setAllFieldsStatusAtOnce('error');

      toastService.addToast({
        type: 'error',
        message: '아이디 또는 이메일을 확인해 주세요.',
      });

      return;
    }

    postFindPassword();
  };

  const postFindPassword = async () => {
    try {
      setIsLoading(true);

      await postUserFindPassword({
        loginId,
        email: ownerEmail,
      });

      toastService.addToast({
        message: '성공적으로 이메일이 발송되었습니다.',
      });
      navigate(PATH.LOGIN);
    } catch (error) {
      const errorCode = (error as TypeErrorResponseData['error']).code;

      if (errorCode !== FIND_PASSWORD.FAILED_TO_SEND_EMAIL) {
        setAllFieldsStatusAtOnce('error');
      }

      toastService.addToast({
        message: (error as TypeErrorResponseData['error']).message,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmptyFields = () => {
    if (!loginId) {
      setLoginIdInputState({
        status: 'error',
        message: '아이디를 입력해 주세요',
      });
    }

    if (!ownerEmail) {
      setOwnerEmailInputState({
        status: 'error',
        message: '이메일을 입력해 주세요.',
      });
    }
  };

  const setAllFieldsStatusAtOnce = (status: TypeInputStatus) => {
    setLoginIdInputState({ status });
    setOwnerEmailInputState({ status });
  };

  return (
    <div className={styles['find-password-page-container']}>
      <div className={styles['find-password-page-wrapper']}>
        <BrandLogo width={147} height={19} className={styles['find-password-logo']} />
        <Title size="accessibility1">비밀번호 초기화</Title>
        <Form className={styles['find-password-form']} onSubmit={handleSubmit}>
          <InputLabel name="아이디" htmlFor="user-id">
            <Input
              placeholder="아이디를 입력해 주세요."
              type="text"
              id="login-id"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              status={loginIdInputState.status}
              validationMessage={loginIdInputState.message ?? ''}
              onFocus={() => setLoginIdInputState({ status: 'default' })}
            />
          </InputLabel>
          <InputLabel name="이메일" htmlFor="user-email">
            <Input
              placeholder="이메일을 입력해 주세요."
              type="email"
              id="owner-email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              status={ownerEmailInputState.status}
              validationMessage={ownerEmailInputState.message ?? ''}
              onFocus={() => setOwnerEmailInputState({ status: 'default' })}
            />
          </InputLabel>
          <div className={styles['find-password-form-info']}>
            {FIND_PASSWORD_INFO_TEXT.map((text) => (
              <InfoText key={text}>{text}</InfoText>
            ))}
          </div>
          <div className={styles['button-container']}>
            <Button color="tertiary" variant="outlined" onClick={() => navigate('/login')}>
              이전으로
            </Button>
            <Button color="secondary" type="submit">
              이메일 전송
            </Button>
          </div>
        </Form>
        <div className={styles['find-id-guide']}>
          <Text color="secondary" size="medium">
            아이디가 기억나지 않으신가요?
          </Text>
          <TextButton href={PATH.FIND_ID} size="tiny">
            아이디 찾기
          </TextButton>
        </div>
      </div>
    </div>
  );
};

export default FindPasswordPage;
